import { extendTheme } from '@chakra-ui/react'
import { mode } from '@chakra-ui/theme-tools'
import defaultTheme from '@chakra-ui/theme'

/**
 * Accent: BGG-style orange (~#FF3C00).
 * Dark UI: elevated purple surfaces with strong borders for WCAG-friendly controls.
 */
const baseButton = defaultTheme.components.Button

function brandSolid(props) {
	return {
		bg: mode('brand.500', 'brand.500')(props),
		color: 'white',
		_hover: {
			bg: mode('brand.600', 'brand.400')(props),
			_disabled: {
				bg: mode('brand.500', 'brand.500')(props),
			},
		},
		_active: {
			bg: mode('brand.700', 'brand.300')(props),
		},
		boxShadow: mode('none', '0 0 0 1px rgba(255,255,255,0.45)')(props),
	}
}

function solidVariant(props) {
	if (props.colorScheme === 'brand') {
		return brandSolid(props)
	}
	return baseButton.variants.solid(props)
}

function brandOutline(props) {
	return {
		border: '2px solid',
		borderColor: mode('brand.500', 'brand.400')(props),
		color: mode('brand.600', 'orange.50')(props),
		bg: mode('transparent', 'whiteAlpha.100')(props),
		_hover: {
			bg: mode('brand.50', 'whiteAlpha.200')(props),
			borderColor: mode('brand.600', 'brand.300')(props),
		},
		_active: {
			bg: mode('brand.100', 'whiteAlpha.300')(props),
		},
	}
}

function outlineVariant(props) {
	if (props.colorScheme === 'brand') {
		return brandOutline(props)
	}
	return baseButton.variants.outline(props)
}

function brandGhost(props) {
	return {
		color: mode('brand.600', 'orange.50')(props),
		bg: 'transparent',
		_hover: {
			bg: mode('brand.50', 'whiteAlpha.200')(props),
		},
		_active: {
			bg: mode('brand.100', 'whiteAlpha.300')(props),
		},
	}
}

function ghostVariant(props) {
	if (props.colorScheme === 'brand') {
		return brandGhost(props)
	}
	return baseButton.variants.ghost(props)
}

const theme = extendTheme({
	config: {
		initialColorMode: 'system',
		useSystemColorMode: true,
	},
	fonts: {
		heading: `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
		body: `'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
	},
	colors: {
		brand: {
			50: '#fff4f0',
			100: '#ffe3d9',
			200: '#ffc9b8',
			300: '#ffa085',
			400: '#ff6b3d',
			500: '#FF3C00',
			600: '#e33500',
			700: '#ba2b00',
			800: '#8f2100',
			900: '#5c1600',
		},
		/** Dark surfaces: clearer steps between canvas, panels, and cards */
		bgg: {
			50: '#f7f6fb',
			100: '#eceaf4',
			200: '#dcd8ec',
			300: '#c4bfdc',
			400: '#a39bc8',
			500: '#8479b0',
			600: '#6b6199',
			700: '#4d4672',
			800: '#3a3558',
			900: '#262336',
			950: '#12101c',
			/** Dark mode supporting text — ~WCAG-friendly on bgg.900 / bgg.950 */
			muted: '#CBD5E0',
		},
	},
	styles: {
		global: (props) => ({
			body: {
				bg: props.colorMode === 'dark' ? 'bgg.950' : 'gray.50',
				color: props.colorMode === 'dark' ? 'gray.50' : 'gray.800',
				WebkitFontSmoothing: 'antialiased',
				MozOsxFontSmoothing: 'grayscale',
			},
		}),
	},
	components: {
		Heading: {
			baseStyle: {
				fontWeight: '600',
				letterSpacing: '-0.02em',
			},
		},
		Button: {
			baseStyle: {
				fontWeight: '600',
				rounded: 'lg',
			},
			defaultProps: {
				colorScheme: 'brand',
			},
			variants: {
				solid: solidVariant,
				outline: outlineVariant,
				ghost: ghostVariant,
			},
		},
		Input: {
			defaultProps: {
				focusBorderColor: 'brand.400',
				variant: 'outline',
			},
			variants: {
				outline: (props) => {
					const base = defaultTheme.components.Input.variants.outline(props)
					return {
						...base,
						field: {
							...base.field,
							bg: mode('white', 'bgg.900')(props),
							borderColor: mode('gray.200', 'whiteAlpha.400')(props),
							color: mode('gray.800', 'gray.50')(props),
							_hover: {
								...base.field._hover,
								borderColor: mode('gray.300', 'whiteAlpha.500')(props),
							},
						},
					}
				},
			},
		},
		Select: {
			defaultProps: {
				focusBorderColor: 'brand.400',
			},
			variants: {
				outline: (props) => {
					const base = defaultTheme.components.Select.variants.outline(props)
					return {
						...base,
						field: {
							...base.field,
							bg: mode('white', 'bgg.900')(props),
							borderColor: mode('gray.200', 'whiteAlpha.400')(props),
							color: mode('gray.800', 'gray.50')(props),
							_hover: {
								...base.field._hover,
								borderColor: mode('gray.300', 'whiteAlpha.500')(props),
							},
						},
					}
				},
			},
		},
		Textarea: {
			defaultProps: {
				focusBorderColor: 'brand.400',
			},
			variants: {
				outline: (props) => {
					const baseField = defaultTheme.components.Input.variants.outline(props).field
					return {
						...baseField,
						bg: mode('white', 'bgg.900')(props),
						borderColor: mode('gray.200', 'whiteAlpha.400')(props),
						color: mode('gray.800', 'gray.50')(props),
						_hover: {
							...baseField._hover,
							borderColor: mode('gray.300', 'whiteAlpha.500')(props),
						},
					}
				},
			},
		},
		Link: {
			baseStyle: {
				fontWeight: '500',
			},
		},
		Accordion: {
			baseStyle: (props) => ({
				container: {
					border: 'none',
				},
				button: {
					borderRadius: 'lg',
					color: mode('gray.800', 'gray.100')(props),
					_expanded: {
						bg: mode('blackAlpha.50', 'whiteAlpha.150')(props),
					},
					_hover: {
						bg: mode('blackAlpha.50', 'whiteAlpha.150')(props),
					},
				},
				panel: {
					pt: 2,
					pb: 4,
				},
			}),
		},
		Table: {
			variants: {
				simple: (props) => ({
					th: {
						borderColor: props.colorMode === 'dark' ? 'whiteAlpha.300' : 'blackAlpha.100',
						fontSize: 'xs',
						textTransform: 'uppercase',
						letterSpacing: '0.06em',
						fontWeight: '600',
						color: props.colorMode === 'dark' ? 'bgg.muted' : 'gray.500',
					},
					td: {
						borderColor: props.colorMode === 'dark' ? 'whiteAlpha.200' : 'blackAlpha.50',
					},
				}),
			},
		},
		Checkbox: {
			defaultProps: {
				colorScheme: 'brand',
			},
		},
		Switch: {
			defaultProps: {
				colorScheme: 'brand',
			},
			baseStyle: (props) => {
				const t = defaultTheme.components.Switch.baseStyle(props)
				return {
					...t,
					track: {
						...t.track,
						bg: mode('gray.200', 'whiteAlpha.300')(props),
						_checked: {
							bg: 'brand.500',
						},
					},
				}
			},
		},
		Modal: {
			baseStyle: (props) => ({
				dialog: {
					borderRadius: 'xl',
					bg: props.colorMode === 'dark' ? 'bgg.900' : 'white',
					borderWidth: props.colorMode === 'dark' ? '1px' : 0,
					borderColor: props.colorMode === 'dark' ? 'whiteAlpha.400' : 'transparent',
					boxShadow: props.colorMode === 'dark' ? '0 25px 50px rgba(0,0,0,0.65)' : undefined,
				},
			}),
		},
	},
	shadows: {
		card: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
		cardDark: '0 1px 3px rgba(0,0,0,0.45)',
	},
})

export default theme
